import { addMonths, addQuarters, addWeeks, addYears, startOfMonth, startOfQuarter, startOfWeek, startOfYear } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Radio, Text } from '@bonusly/design-system';

import { Reveal } from '../../../../../../components/display/reveal';
import { Award, BudgetPeriod } from '../../../../../../types/bonus';

interface AwardOptionProps {
	award: Award;
	isChecked: boolean;
	onClick: VoidFunction;
}

const getNextResetDate = (period: BudgetPeriod) => {
	const today = new Date();

	switch (period) {
		case BudgetPeriod.Week:
			return startOfWeek(addWeeks(today, 1));

		case BudgetPeriod.Month:
			return startOfMonth(addMonths(today, 1));

		case BudgetPeriod.Quarter:
			return startOfQuarter(addQuarters(today, 1));

		case BudgetPeriod.Year:
			return startOfYear(addYears(today, 1));

		default:
			return null;
	}
};

export const AwardOption: FC<AwardOptionProps> = ({ award, isChecked, onClick }) => {
	const { t } = useTranslation('awards', { keyPrefix: 'give' });
	const isInfinite = award.giver?.infinite_budget;
	const isDisabled = !isInfinite && !award.giving_balance;
	const titleColor = isDisabled ? 'gray.500' : isChecked ? 'blue.500' : 'gray.900';

	const nextResetDate = getNextResetDate(award.budget_period);
	const keys = ['remaining'];

	let remaining = 0,
		total = 0;

	if (award.allowance === null) {
		keys.push('unlimited');
	} else if (award.bonus_amount_changeable) {
		keys.push('points', award.budget_period);

		remaining = award.giving_balance;
		total = award.allowance;
	} else {
		keys.push('awards', award.budget_period);

		// The award amount should always be a number at this point but our types
		// are very messy. This should be fixed when we update awards to use newer endpoints.
		total = Math.trunc(award.allowance / (award.amount ?? 1));
		remaining = Math.trunc(award.giving_balance / (award.amount ?? 1));
	}

	return (
		<Flex
			as="label"
			borderBottomColor="gray.100"
			borderBottomStyle="solid"
			borderBottomWidth="1px"
			cursor="pointer"
			gap={1}
			px={5}
			py={4}
			w="full"
			{...(isDisabled && {
				bg: 'gray.25',
			})}
		>
			<Box>
				<Radio isChecked={isChecked} isDisabled={isDisabled} onChange={onClick} mt={1.5} value={award.id} />
			</Box>

			<Box flexGrow={1}>
				<Text color={titleColor} fontSize="xl" fontWeight="semibold" lineHeight={8} noOfLines={1} mb={1.5}>
					{award.name}
				</Text>

				<Reveal if={award.description}>
					<Text mb={1}>{award.description}</Text>
				</Reveal>

				<Text size="sm" color="text.softer">
					{t(keys.join('_'), { remaining, total, date: nextResetDate })}
				</Text>
			</Box>
		</Flex>
	);
};
